import type { VideoContentKind } from "@healthos/shared";
import { useEffect, useRef } from "react";
import { usePrimaryAudio } from "../ambience/use-primary-audio";
import { useMediaPlayer } from "./media-player-provider";
import type { MediaWatchDetail } from "./media-player-types";

export interface ResetMediaLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (detail: MediaWatchDetail) => void;
  onStarted?: (detail: MediaWatchDetail) => void;
  reset: {
    title: string;
    description: string;
    duration: number;
    videoUrl?: string;
    kind?: VideoContentKind;
  };
}

/**
 * Keeps the legacy ResetModal call sites small while moving actual playback to
 * the persistent web media session. A minimized reset outlives this bridge and
 * can follow the member to another route.
 */
export function ResetMediaLauncher({
  isOpen,
  onClose,
  onComplete,
  onStarted,
  reset,
}: ResetMediaLauncherProps) {
  const mediaPlayer = useMediaPlayer();
  const onPlayingChange = usePrimaryAudio("reset", 0.03);
  const callbacksRef = useRef({ onClose, onComplete, onStarted, onPlayingChange });

  useEffect(() => {
    callbacksRef.current = { onClose, onComplete, onStarted, onPlayingChange };
  }, [onClose, onComplete, onStarted, onPlayingChange]);

  useEffect(() => {
    if (!isOpen || !reset.videoUrl) return;
    mediaPlayer.playVideo({
      title: reset.title,
      description: reset.description,
      durationSeconds: reset.duration || null,
      videoUrl: reset.videoUrl,
      kind: reset.kind ?? "Reset",
      onDismiss: () => callbacksRef.current.onClose(),
      onStarted: (detail) => callbacksRef.current.onStarted?.(detail),
      onComplete: (detail) => callbacksRef.current.onComplete(detail),
      onPlayingChange: (playing) => callbacksRef.current.onPlayingChange(playing),
    });
  }, [
    isOpen,
    mediaPlayer,
    reset.description,
    reset.duration,
    reset.kind,
    reset.title,
    reset.videoUrl,
  ]);

  return null;
}
